import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { Button, Icon, Spinner, cx } from "../../components/ui";
import { LABEL, urutkanDetail } from "./todoDetail";

// ─── To-Do List kelas (Fase C) ───────────────────────────────────────────────
//
// Tujuh tahap kelas, masing-masing: Do/Don't, aktivitas, act of bonding, waktu.
// Hanya tampil bagi trainer dalam MASA PENGAWASAN (`wajib`): baginya tombol Simpan absensi
// terkunci sampai to-do ini TERSIMPAN dengan ketujuh tahap berisi aktivitas. Panel ini
// melaporkan keadaan yang tersimpan lewat `onStatus`; server menjaga hal yang sama.
//
// Aktivitas & act of bonding boleh dipilih dari bank ATAU "✎ Tulis sendiri" — daftar tak
// akan pernah mencakup semua yang terjadi di kelas, dan memaksa memilih yang "paling mirip"
// membuat catatannya tak jujur. Tulisan sendiri ditandai, dan aktivitasnya boleh diberi
// keterangan cara menjalankannya (opsional).
//
// Trainer mengisi ini sambil mengajar, sering dari HP. Karena itu:
//   • Dropdown tetap <select> bawaan, bukan combobox buatan — pemilih bawaan HP sudah
//     punya gulir & ketik-cari sendiri, dan hampir selalu lebih enak dari tiruannya.
//   • Memilih aktivitas LANGSUNG memuat detailnya dan mengisikan saran act of bonding,
//     jadi satu pilihan mengurangi satu pekerjaan lain.
//   • Waktu punya tombol cepat; mengetik angka di HP sambil berdiri itu mahal.

const DO_PILIHAN = [
  { nilai: "Do", label: "Do", warna: "border-green-500 bg-green-500" },
  { nilai: "Dont", label: "Don't", warna: "border-red-500 bg-red-500" }
];

const WAKTU_CEPAT = ["5 min", "10 min", "15 min", "20 min"];

// Saran act of bonding yang melekat pada aktivitas itu sendiri. Untuk Sitting
// Arrangement inilah SATU-SATUNYA sumbernya — berkas asal memang menaruh bonding-nya
// di tiap susunan duduk ("Bonding Move"), bukan di daftar dropdown tersendiri.
const saranBondingDari = (detail) =>
  String((detail && (detail["Bonding Move"] || detail["Act of Bonding"])) || "").trim();

function PanelDetail({ data, onTutup }) {
  if (!data) return null;
  const baris = urutkanDetail(data.detail || {});
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-black text-primary">{data.nama}</p>
          <p className="text-[11px] text-slate-400">Cara menjalankannya</p>
        </div>
        <button type="button" onClick={onTutup} title="Tutup" className="text-slate-400 hover:text-red-500">
          <Icon name="close" className="text-sm" />
        </button>
      </div>
      {data.videoUrl && (
        <a
          href={data.videoUrl}
          target="_blank"
          rel="noreferrer"
          className="mb-2 inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-black text-primary hover:underline"
        >
          <Icon name="play_circle" className="text-sm" />
          Buka video contoh
        </a>
      )}
      <dl className="space-y-1.5">
        {baris.map(([k, v]) => (
          <div key={k}>
            <dt className="text-[11px] font-black uppercase tracking-wide text-slate-400">
              {LABEL[k] || k}
            </dt>
            <dd className="whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// Nilai pilihan "✎ Tulis sendiri…" di dropdown. Bukan teks yang mungkin ada di bank.
const TULIS = "__tulis_sendiri__";
const MIN_JUDUL = 3;
const KOTAK = "w-full rounded-lg border border-slate-200 bg-transparent px-2 py-2 text-xs dark:border-slate-700";

function BarisTahap({ tahap, isi, saran, onUbah, onPilihAktivitas, onLihatDetail, sedangDilihat }) {
  const [penjelasan, setPenjelasan] = useState(false);
  const pilihan = tahap.aktivitas || [];
  const aktif = isi.activityManual ? null : pilihan.find((a) => a.teks === isi.activity) || null;
  const terisi = !!(isi.doFlag || isi.activity || isi.bonding || isi.waktu);
  // Saran dari aktivitas yang dipilih ikut ditawarkan walau tak ada di daftar dropdown.
  const bondingOpsi = [...(tahap.bonding || [])];
  if (saran && !bondingOpsi.includes(saran)) bondingOpsi.unshift(saran);
  // Isian lama yang teksnya tak (lagi) ada di daftar tetap ditampilkan apa adanya — bukan
  // diam-diam terlihat kosong padahal tersimpan.
  const aktivitasLama = !isi.activityManual && isi.activity && !pilihan.some((a) => a.teks === isi.activity);
  const bondingLama = !isi.bondingManual && isi.bonding && !bondingOpsi.includes(isi.bonding);
  const judul = String(isi.activity || "").trim();
  const judulPendek = isi.activityManual && judul.length > 0 && judul.length < MIN_JUDUL;

  return (
    <div className={cx(
      "rounded-xl border p-3 transition",
      terisi
        ? "border-green-500/40 bg-green-500/[0.04]"
        : "border-slate-200 dark:border-slate-700"
    )}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-black">
            <span className={cx(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]",
              terisi ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500 dark:bg-slate-700"
            )}>
              {terisi ? "✓" : tahap.urutan}
            </span>
            <span className="truncate">{tahap.tahap}</span>
            {tahap.deskripsi && (
              <button
                type="button"
                onClick={() => setPenjelasan((v) => !v)}
                title="Apa maksud tahap ini?"
                className="shrink-0 text-slate-300 hover:text-primary"
              >
                <Icon name="help" className="text-sm" />
              </button>
            )}
          </p>
          {penjelasan && tahap.deskripsi && (
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{tahap.deskripsi}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          {DO_PILIHAN.map((d) => (
            <button
              key={d.nilai}
              type="button"
              title={d.nilai === "Do" ? "Ini yang saya lakukan" : "Ini yang saya hindari"}
              onClick={() => onUbah({ doFlag: isi.doFlag === d.nilai ? "" : d.nilai })}
              className={cx(
                "rounded-lg border px-2 py-1 text-xs font-black transition",
                isi.doFlag === d.nilai
                  ? `${d.warna} text-white`
                  : "border-slate-200 text-slate-400 hover:border-primary/40 dark:border-slate-700"
              )}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex gap-1">
          <select
            aria-label={`Aktivitas ${tahap.tahap}`}
            value={isi.activityManual ? TULIS : (isi.activity || "")}
            onChange={(e) => (e.target.value === TULIS
              ? onUbah({ activityManual: true, activity: "", activityId: null })
              : onPilihAktivitas(e.target.value))}
            className={KOTAK}
          >
            <option value="">— pilih aktivitas —</option>
            {aktivitasLama && <option value={isi.activity}>{isi.activity} (tak ada di daftar)</option>}
            {pilihan.map((a) => (
              <option key={a.teks} value={a.teks}>{a.teks}</option>
            ))}
            <option value={TULIS}>✎ Tulis sendiri…</option>
          </select>
          {/* Hanya aktivitas yang punya halaman detail yang bisa dibuka. "Review +
              Closing" berisi kalimat penutup, bukan aktivitas ber-langkah. */}
          {aktif && aktif.activityId && (
            <button
              type="button"
              title="Lihat cara menjalankannya"
              onClick={() => onLihatDetail(aktif.activityId)}
              className={cx(
                "shrink-0 rounded-lg border px-2.5 transition",
                sedangDilihat === aktif.activityId
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-slate-200 text-slate-400 hover:text-primary dark:border-slate-700"
              )}
            >
              <Icon name="menu_book" className="text-sm" />
            </button>
          )}
        </div>
        {isi.activityManual && (
          <div className="space-y-1 rounded-lg border border-dashed border-primary/30 p-2">
            <input
              aria-label={`Aktivitas tulisan sendiri ${tahap.tahap}`}
              value={isi.activity || ""}
              onChange={(e) => onUbah({ activity: e.target.value })}
              maxLength={255}
              placeholder="Tulis kegiatan yang Anda lakukan…"
              className={KOTAK}
            />
            {judulPendek && (
              <p className="text-[11px] font-bold text-red-500">Minimal {MIN_JUDUL} huruf.</p>
            )}
            <textarea
              aria-label={`Keterangan ${tahap.tahap}`}
              value={isi.keterangan || ""}
              onChange={(e) => onUbah({ keterangan: e.target.value })}
              maxLength={1000}
              rows={2}
              placeholder="Keterangan (opsional): bagaimana kegiatan ini dijalankan"
              className={KOTAK}
            />
          </div>
        )}

        <select
          aria-label={`Act of bonding ${tahap.tahap}`}
          value={isi.bondingManual ? TULIS : (isi.bonding || "")}
          onChange={(e) => (e.target.value === TULIS
            ? onUbah({ bondingManual: true, bonding: "" })
            : onUbah({ bondingManual: false, bonding: e.target.value }))}
          className={KOTAK}
        >
          <option value="">— act of bonding —</option>
          {bondingLama && <option value={isi.bonding}>{isi.bonding} (tak ada di daftar)</option>}
          {bondingOpsi.map((b) => (
            <option key={b} value={b}>{b === saran ? `★ ${b}` : b}</option>
          ))}
          <option value={TULIS}>✎ Tulis sendiri…</option>
        </select>
        {isi.bondingManual && (
          <input
            aria-label={`Act of bonding tulisan sendiri ${tahap.tahap}`}
            value={isi.bonding || ""}
            onChange={(e) => onUbah({ bonding: e.target.value })}
            maxLength={255}
            placeholder="Tulis act of bonding Anda…"
            className={KOTAK}
          />
        )}

        <div className="flex flex-wrap items-center gap-1">
          {WAKTU_CEPAT.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onUbah({ waktu: isi.waktu === w ? "" : w })}
              className={cx(
                "rounded-lg border px-2 py-1 text-[11px] font-black transition",
                isi.waktu === w
                  ? "border-primary bg-primary text-white"
                  : "border-slate-200 text-slate-400 hover:border-primary/40 dark:border-slate-700"
              )}
            >
              {w.replace(" min", "′")}
            </button>
          ))}
          <input
            value={isi.waktu || ""}
            onChange={(e) => onUbah({ waktu: e.target.value })}
            placeholder="atau ketik…"
            className="w-24 rounded-lg border border-slate-200 bg-transparent px-2 py-1 text-[11px] dark:border-slate-700"
          />
        </div>
      </div>
    </div>
  );
}

// Isian dari server → isi formulir. Satu tempat, dipakai saat memuat & menyalin.
const keIsi = (i) => ({
  doFlag: i.doFlag || "",
  activity: i.activity || "",
  activityId: i.activityId || null,
  activityManual: !!i.activityManual,
  bonding: i.bonding || "",
  bondingManual: !!i.bondingManual,
  waktu: i.waktu || "",
  keterangan: i.keterangan || ""
});
const beraktivitas = (items) => (items || []).filter((i) => String(i.activity || "").trim()).length;

export default function TodoListPanel({ classIds, date, setToast, wajib = false, onStatus }) {
  // Yang diawasi tak perlu mencari panelnya: langsung terbuka.
  const [terbuka, setTerbuka] = useState(!!wajib);
  const [memuat, setMemuat] = useState(false);
  const [menyimpan, setMenyimpan] = useState(false);
  const [opsi, setOpsi] = useState(null);
  const [isi, setIsi] = useState({});          // tahap -> lihat keIsi()
  const [saran, setSaran] = useState({});      // tahap -> saran act of bonding dari aktivitasnya
  const [detail, setDetail] = useState(null);
  const [sudahDisimpan, setSudahDisimpan] = useState(false);

  // Satu kartu biasanya satu kelas. Kalau lebih dari satu, trainer memilih — menebak
  // berarti to-do bisa mendarat di kelas yang keliru.
  // useMemo: tanpa ini `daftar` jadi array baru tiap render dan efek di bawah ikut
  // berjalan terus-menerus.
  const daftar = useMemo(() => (Array.isArray(classIds) ? classIds : []), [classIds]);
  const [classId, setClassId] = useState(daftar.length === 1 ? daftar[0] : null);

  useEffect(() => {
    if (daftar.length === 1 && classId !== daftar[0]) setClassId(daftar[0]);
  }, [daftar, classId]);

  useEffect(() => {
    if (wajib) setTerbuka(true);
  }, [wajib]);

  // Lewat ref: pemanggil boleh mengirim fungsi baru tiap render tanpa memicu muat ulang.
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  // berlaku: true = kelas ini wajib/bisa diisi, false = tak punya bank (tak dikunci),
  // null = tak diketahui (galat) — halaman memperlakukannya sebagai tak dikunci.
  const lapor = (berlaku, tahapTersimpan = 0) => {
    if (onStatusRef.current) onStatusRef.current({ berlaku, tahapTersimpan });
  };

  const muat = useCallback(async () => {
    if (!classId || !date) return;
    setMemuat(true);
    try {
      const [o, t] = await Promise.all([
        api.getTodoOptions(classId),
        api.getTodo(classId, date)
      ]);
      setOpsi(o && o.success ? o : null);
      const awal = {};
      ((t && t.data && t.data.items) || []).forEach((i) => { awal[i.tahap] = keIsi(i); });
      setIsi(awal);
      setSudahDisimpan(!!(t && t.data));
      if (o && o.success) lapor(!!o.siap, o.siap ? beraktivitas(t && t.data && t.data.items) : 0);
      else lapor(null);
    } catch (err) {
      setToast?.({ type: "error", message: err.message || "Gagal memuat To-Do List" });
      setOpsi(null);
      lapor(null);
    } finally {
      setMemuat(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, date, setToast]);

  useEffect(() => {
    if (terbuka) muat();
  }, [terbuka, muat]);

  function ubah(tahap, patch) {
    setIsi((s) => ({ ...s, [tahap]: { ...(s[tahap] || {}), ...patch } }));
  }

  // Memilih aktivitas sekaligus: menyimpan pilihan, memuat detailnya, dan mengisikan
  // saran act of bonding bila kolomnya masih kosong. Satu tindakan trainer, tiga hasil.
  async function pilihAktivitas(tahap, teks) {
    const p = (tahap.aktivitas || []).find((x) => x.teks === teks);
    const aid = (p && p.activityId) || null;
    ubah(tahap.tahap, { activity: teks, activityId: aid, activityManual: false, keterangan: "" });
    if (!aid) {
      setSaran((s) => ({ ...s, [tahap.tahap]: "" }));
      return;
    }
    try {
      const r = await api.getTodoActivity(aid);
      const d = r && r.success ? r.data : null;
      const s = saranBondingDari(d && d.detail);
      setSaran((x) => ({ ...x, [tahap.tahap]: s }));
      setIsi((cur) => {
        const skr = cur[tahap.tahap] || {};
        // Bonding yang sudah dipilih — atau sedang ditulis sendiri — tak ditimpa saran.
        if (!s || skr.bonding || skr.bondingManual) return cur;
        return { ...cur, [tahap.tahap]: { ...skr, bonding: s.slice(0, 255) } };
      });
    } catch (err) {
      /* saran bonding hanya kenyamanan — kegagalannya tak boleh menghentikan pengisian */
    }
  }

  async function lihatDetail(id) {
    if (detail && detail.id === id) return setDetail(null);
    try {
      const r = await api.getTodoActivity(id);
      setDetail(r && r.success ? r.data : null);
    } catch (err) {
      setToast?.({ type: "error", message: err.message || "Gagal memuat detail" });
    }
  }

  async function salinTerakhir() {
    try {
      const r = await api.getTodoTerakhir(classId, date);
      if (!r || !r.data) {
        return setToast?.({ type: "error", message: "Belum ada to-do list sebelumnya untuk kelas ini." });
      }
      const awal = {};
      (r.data.items || []).forEach((i) => { awal[i.tahap] = keIsi(i); });
      setIsi(awal);
      setToast?.({ type: "success", message: `Disalin dari pertemuan ${r.data.tanggal}. Sesuaikan bila perlu.` });
    } catch (err) {
      setToast?.({ type: "error", message: err.message || "Gagal menyalin" });
    }
  }

  async function simpan() {
    setMenyimpan(true);
    try {
      const items = (opsi?.tahap || [])
        .map((t) => ({ tahap: t.tahap, ...(isi[t.tahap] || {}) }))
        // Tahap yang benar-benar kosong tak dikirim: yang terkirim adalah apa yang
        // trainer isi, bukan tujuh baris hampa yang menyamar sebagai sudah diisi.
        .filter((i) => i.doFlag || String(i.activity || "").trim() || String(i.bonding || "").trim() || i.waktu);
      if (!items.length) {
        setMenyimpan(false);
        return setToast?.({ type: "error", message: "Belum ada satu pun tahap yang diisi." });
      }
      const pendek = items.filter((i) => {
        const n = String(i.activity || "").trim().length;
        return i.activityManual && n > 0 && n < MIN_JUDUL;
      });
      if (pendek.length) {
        setMenyimpan(false);
        return setToast?.({
          type: "error",
          message: `Aktivitas tulisan sendiri minimal ${MIN_JUDUL} huruf (tahap ${pendek.map((i) => i.tahap).join(", ")}).`
        });
      }
      const r = await api.simpanTodo({ classId, date, items });
      if (!r.success) throw new Error(r.message || "Gagal menyimpan");
      setSudahDisimpan(true);
      lapor(true, r.tahapBeraktivitas != null ? Number(r.tahapBeraktivitas) : beraktivitas(items));
      setToast?.({ type: "success", message: r.message });
    } catch (err) {
      setToast?.({ type: "error", message: err.message || "Gagal menyimpan To-Do List" });
    } finally {
      setMenyimpan(false);
    }
  }

  // Kartu yang tak bersumber dari kelas (roster jalur teks lama) tak bisa dipetakan ke
  // satu kelas. Disembunyikan sepenuhnya alih-alih menampilkan formulir yang pasti gagal.
  if (!daftar.length) return null;

  const daftarTahap = opsi?.tahap || [];
  // Kemajuan dihitung dari tahap yang BERISI AKTIVITAS — ukuran yang sama dengan kunci
  // Simpan, supaya "7/7" di sini tak pernah berarti lain dengan yang membuka tombolnya.
  const terisi = daftarTahap.filter((t) => String((isi[t.tahap] || {}).activity || "").trim()).length;
  const persen = daftarTahap.length ? Math.round((terisi / daftarTahap.length) * 100) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setTerbuka((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3"
      >
        <span className="flex min-w-0 items-center gap-2 text-sm font-black">
          <Icon name="checklist" className="text-primary" />
          <span className="truncate">To-Do List Kelas</span>
          {wajib && (
            <span className="shrink-0 rounded-lg bg-red-500/15 px-2 py-0.5 text-xs text-red-600">WAJIB</span>
          )}
          {sudahDisimpan && (
            <span className="shrink-0 rounded-lg bg-green-500/15 px-2 py-0.5 text-xs text-green-600">
              tersimpan
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {opsi?.siap && (
            <>
              <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-slate-200 sm:block dark:bg-slate-700">
                <span
                  className={cx("block h-full rounded-full transition-all",
                    terisi === daftarTahap.length ? "bg-green-500" : "bg-primary")}
                  style={{ width: `${persen}%` }}
                />
              </span>
              <span className="text-xs font-black text-slate-400">{terisi}/{daftarTahap.length} tahap</span>
            </>
          )}
          <Icon name={terbuka ? "expand_less" : "expand_more"} className="text-slate-400" />
        </span>
      </button>

      {terbuka && (
        <div className="space-y-3 border-t border-slate-200 p-4 dark:border-slate-700">
          {daftar.length > 1 && (
            <div>
              <p className="mb-1 text-xs font-black text-amber-600">
                Kartu ini menampung lebih dari satu kelas — pilih dulu yang mana.
              </p>
              <select
                value={classId || ""}
                onChange={(e) => setClassId(Number(e.target.value) || null)}
                className="w-full rounded-lg border border-slate-200 bg-transparent px-2 py-1.5 text-xs dark:border-slate-700"
              >
                <option value="">— pilih kelas —</option>
                {daftar.map((id) => (
                  <option key={id} value={id}>Kelas #{id}</option>
                ))}
              </select>
            </div>
          )}

          {memuat && <div className="py-6"><Spinner /></div>}

          {!memuat && opsi && !opsi.siap && (
            <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-700 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300">
              {opsi.pesan}
            </p>
          )}

          {!memuat && opsi?.siap && (
            <>
              <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
                <p className="text-xs leading-relaxed text-slate-500">
                  Catat rencana tujuh tahap kelas ini. Tekan{" "}
                  <Icon name="help" className="inline align-text-bottom text-sm" /> untuk tahu maksud
                  tiap tahap, dan{" "}
                  <Icon name="menu_book" className="inline align-text-bottom text-sm" /> untuk melihat
                  cara menjalankan aktivitas yang dipilih. Tak ada yang cocok? Pilih{" "}
                  <b>✎ Tulis sendiri</b>.{" "}
                  <b>Do</b> = yang Anda lakukan, <b>Don&apos;t</b> = yang Anda hindari.
                </p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-400">
                    Bank <b>{opsi.bank}</b> · {opsi.kelas?.nama}
                  </span>
                  <button
                    type="button"
                    onClick={salinTerakhir}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-black text-slate-500 transition hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:bg-slate-900"
                  >
                    <Icon name="content_copy" className="text-sm" />
                    Salin dari pertemuan lalu
                  </button>
                </div>
              </div>

              {detail && <PanelDetail data={detail} onTutup={() => setDetail(null)} />}

              {daftarTahap.map((t) => (
                <BarisTahap
                  key={t.tahap}
                  tahap={t}
                  isi={isi[t.tahap] || {}}
                  saran={saran[t.tahap] || ""}
                  onUbah={(p) => ubah(t.tahap, p)}
                  onPilihAktivitas={(teks) => pilihAktivitas(t, teks)}
                  onLihatDetail={lihatDetail}
                  sedangDilihat={detail?.id || null}
                />
              ))}

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                {wajib ? (
                  <p className="text-xs font-bold text-red-600">
                    Wajib (masa pengawasan) — tombol Simpan absensi terbuka setelah To-Do List ini
                    disimpan dengan ketujuh tahap berisi aktivitas, dari daftar atau tulisan sendiri.
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    Belum wajib — mengisi ini tidak menghalangi tombol Simpan absensi.
                  </p>
                )}
                <Button onClick={simpan} disabled={menyimpan}>
                  <Icon name="save" />
                  {menyimpan ? "Menyimpan…" : "Simpan To-Do List"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
